// FormSettingsVersions — the read-only "Form versions" tab of the settings
// drawer: a summary block (status chip · current version · last updated by/at)
// over a Version History table (Version · Last updated · Updated By, from the
// detail's versions[] rows). Pure view — no save handle; everything renders
// from the form detail passed down by the drawer.

import { useTranslation } from 'react-i18next';
import { Chip, DataTable, Icon, type ChipVariant } from '@/components/common';
import type { FormDetail, FormStatus, FormVersionEntry } from '@/api/endpoints/formsApi';

const STATUS_VARIANT: Record<FormStatus, ChipVariant> = {
  draft: 'neutral',
  published: 'success',
  suspended: 'warning',
};

interface FormSettingsVersionsProps {
  /** Form detail — undefined while it loads. */
  detail?: FormDetail;
}

const FormSettingsVersions = ({ detail }: FormSettingsVersionsProps) => {
  const { t, i18n } = useTranslation();

  const fmtDateTime = (iso: string | null) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime())
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'medium' }).format(
          d,
        )
      : '—';
  };

  const status = detail?.design?.versionState;
  const summary = [
    {
      key: 'status',
      label: t('text_form_status'),
      value: status ? (
        <Chip variant={STATUS_VARIANT[status]}>{t(`forms_status_${status}`)}</Chip>
      ) : (
        '—'
      ),
    },
    {
      key: 'version',
      label: t('text_current_version'),
      value: detail?.design?.version ?? '—',
    },
    {
      key: 'updated-by',
      label: t('text_last_updated_by'),
      value: detail?.lastUpdatedBy?.fullName || '—',
    },
    {
      key: 'updated-at',
      label: t('text_last_updated'),
      value: fmtDateTime(detail?.updatedAt ?? null),
    },
  ];

  return (
    <div className='form-versions'>
      <dl className='form-versions__summary'>
        {summary.map((row) => (
          <div key={row.key} className='form-versions__row'>
            <dt className='form-versions__label'>{row.label}</dt>
            <dd className='form-versions__value'>{row.value}</dd>
          </div>
        ))}
      </dl>

      <section className='form-versions__history'>
        <h3 className='form-versions__history-title'>{t('text_version_history')}</h3>
        <DataTable<FormVersionEntry>
          columns={[
            {
              key: 'version',
              label: t('table_header_version'),
              render: (v) => v.version,
            },
            {
              key: 'updated',
              label: t('table_header_last_updated'),
              render: (v) => fmtDateTime(v.updatedAt),
            },
            {
              key: 'by',
              label: t('table_header_updated_by'),
              render: (v) => v.updatedBy?.fullName || '—',
            },
          ]}
          rows={detail?.versions ?? []}
          rowKey={(v) => v.id}
          loading={!detail}
          loadingText={t('text_loading')}
          minWidth={480}
          empty={
            <>
              <Icon name='history' size={26} />
              {t('form_settings_versions_empty')}
            </>
          }
        />
      </section>
    </div>
  );
};

export default FormSettingsVersions;
